<template>
  <div class="metricas-aniversario-container">
    <!-- Header com Breadcrumb -->
    <div class="page-header mb-3">
      <div class="d-flex align-items-center justify-content-between">
        <div class="flex-grow-1">
          <b-breadcrumb class="breadcrumb-custom mb-2">
            <b-breadcrumb-item :to="{ name: 'campaign-ia' }">
              <feather-icon icon="ZapIcon" size="16" />
              Campanhas Inteligentes
            </b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'campaign-ia-aniversario', query: { loja: lojaSelecionada, nomeLoja: nomeLojaSelecionada } }">
              Campanhas de Aniversário
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              Métricas
            </b-breadcrumb-item>
          </b-breadcrumb>
          <h2 class="page-title mb-1">
            <feather-icon
              icon="BarChart2Icon"
              size="28"
              class="text-primary mr-2"
            />
            Métricas - Campanha de Aniversário
          </h2>
          <p class="text-muted mb-0">
            Acompanhe o desempenho da sua campanha de aniversário
          </p>
        </div>
      </div>
    </div>

    <!-- Informação da Loja Selecionada -->
    <b-alert v-if="lojaSelecionada" variant="info" show class="loja-info mb-4">
      <div class="d-flex align-items-center">
        <feather-icon icon="StoreIcon" size="18" class="mr-2" />
        <div>
          <strong>Loja Selecionada:</strong> {{ nomeLojaSelecionada }}
        </div>
      </div>
    </b-alert>

    <!-- Loading -->
    <div v-if="carregando" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Carregando métricas...</p>
    </div>

    <!-- Sem Campanha Ativa -->
    <div v-else-if="!campanhaAtiva" class="text-center py-5">
      <b-card class="shadow-sm">
        <div class="empty-icon mb-3">
          <feather-icon icon="AlertCircleIcon" size="64" class="text-muted" />
        </div>
        <h4>Nenhuma Campanha Ativa</h4>
        <p class="text-muted">
          Não há campanha de aniversário ativa para esta loja. Configure uma campanha primeiro.
        </p>
        <b-button
          variant="primary"
          :to="{ name: 'campaign-ia-aniversario', query: { loja: lojaSelecionada, nomeLoja: nomeLojaSelecionada } }"
        >
          <feather-icon icon="SettingsIcon" size="16" class="mr-50" />
          Configurar Campanha
        </b-button>
      </b-card>
    </div>

    <!-- Conteúdo Principal -->
    <template v-else>
      <!-- Informações da Campanha -->
      <b-card class="campanha-info-card shadow-sm mb-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center">
            <div class="status-badge-active mr-3">
              <feather-icon icon="CheckCircleIcon" size="24" />
            </div>
            <div>
              <h4 class="mb-0">{{ campanhaAtiva.name }}</h4>
              <small class="text-success">
                <feather-icon icon="ActivityIcon" size="14" />
                Campanha Ativa
              </small>
            </div>
          </div>
          <b-button
            variant="outline-secondary"
            size="sm"
            :to="{ name: 'campaign-ia-aniversario', query: { loja: lojaSelecionada, nomeLoja: nomeLojaSelecionada } }"
          >
            <feather-icon icon="SettingsIcon" size="14" class="mr-50" />
            Configurações
          </b-button>
        </div>

        <!-- Configurações da Campanha -->
        <b-row class="config-info">
          <b-col md="2" sm="6" class="mb-3">
            <div class="info-item">
              <feather-icon icon="ShoppingBagIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Template 1 (Recentes)</small>
                <strong>{{ campanhaAtiva.template_name_recent || campanhaAtiva.template_name }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="2" sm="6" class="mb-3">
            <div class="info-item">
              <feather-icon icon="UsersIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Template 2 (Inativos)</small>
                <strong>{{ campanhaAtiva.template_name_inactive || campanhaAtiva.template_name }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="2" sm="6" class="mb-3">
            <div class="info-item">
              <feather-icon icon="CalendarIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Período de Compra</small>
                <strong>{{ campanhaAtiva.recent_purchase_days || campanhaAtiva.purchase_days || 15 }} dias</strong>
              </div>
            </div>
          </b-col>
          <b-col md="2" sm="6" class="mb-3">
            <div class="info-item">
              <feather-icon icon="ClockIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Horário de Envio</small>
                <strong>{{ formatarHorario(campanhaAtiva.send_time) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="2" sm="6" class="mb-3">
            <div class="info-item">
              <feather-icon icon="TrendingUpIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Limite Diário</small>
                <strong>{{ campanhaAtiva.daily_limit }} mensagens</strong>
              </div>
            </div>
          </b-col>
          <b-col md="2" sm="6" class="mb-3">
            <div class="info-item">
              <feather-icon icon="FileTextIcon" size="18" class="text-primary mr-2" />
              <div>
                <small class="text-muted d-block">Templates Configurados</small>
                <strong>2 templates</strong>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Métricas de Envio -->
      <b-card class="metricas-card shadow-sm mb-4">
        <h5 class="mb-3">
          <feather-icon icon="SendIcon" size="20" class="mr-2" />
          Métricas de Envio
        </h5>
        <b-row v-if="metricas">
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card metrica-card-clickable" @click="abrirModalEnviosComFiltro('sent')">
              <div class="metrica-icon bg-success">
                <feather-icon icon="SendIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ metricas.total_sent || 0 }}</h4>
                <small class="text-muted">Enviados</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card metrica-card-clickable" @click="abrirModalEnviosComFiltro('scheduled')">
              <div class="metrica-icon bg-info">
                <feather-icon icon="ClockIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ metricas.total_scheduled || 0 }}</h4>
                <small class="text-muted">Agendados</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card metrica-card-clickable" @click="abrirModalEnviosComFiltro('errors')">
              <div class="metrica-icon bg-danger">
                <feather-icon icon="AlertCircleIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ totalErros }}</h4>
                <small class="text-muted">Erros</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card">
              <div class="metrica-icon bg-primary">
                <feather-icon icon="TrendingUpIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ metricas.sent_today || 0 }}</h4>
                <small class="text-muted">Enviados Hoje</small>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Seção de Conversões com Janela de Atribuição -->
      <b-card class="conversoes-card shadow-sm mb-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h5 class="mb-0">
            <feather-icon icon="ShoppingCartIcon" size="20" class="mr-2" />
            Conversões
          </h5>
          <div class="d-flex align-items-center">
            <small class="text-muted mr-2">Janela de Atribuição:</small>
            <b-form-select
              v-model="janelaAtribuicao"
              :options="opcoesJanelaAtribuicao"
              size="sm"
              style="width: 120px;"
              @change="carregarMetricasConversao"
            />
            <feather-icon
              icon="HelpCircleIcon"
              size="16"
              class="text-muted ml-2 cursor-pointer"
              v-b-tooltip.hover.top="'A janela de atribuição define o período após o envio da mensagem em que uma compra será considerada como conversão da campanha.'"
            />
          </div>
        </div>

        <!-- Loading de Conversões -->
        <div v-if="carregandoConversoes" class="text-center py-3">
          <b-spinner small variant="primary" />
          <small class="text-muted ml-2">Calculando conversões...</small>
        </div>

        <!-- Cards de Conversão -->
        <b-row v-else-if="metricasConversao">
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card conversao-card">
              <div class="metrica-icon bg-primary">
                <feather-icon icon="ShoppingCartIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ metricasConversao.total_conversoes || 0 }}</h4>
                <small class="text-muted">Total Conversões</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card conversao-card">
              <div class="metrica-icon bg-success">
                <feather-icon icon="PercentIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ metricasConversao.taxa_conversao || 0 }}%</h4>
                <small class="text-muted">Taxa de Conversão</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card conversao-card">
              <div class="metrica-icon bg-warning">
                <feather-icon icon="DollarSignIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ formatarMoeda(metricasConversao.valor_total) }}</h4>
                <small class="text-muted">Valor Total</small>
              </div>
            </div>
          </b-col>
          <b-col md="3" sm="6" class="mb-3">
            <div class="metrica-card conversao-card">
              <div class="metrica-icon bg-info">
                <feather-icon icon="CreditCardIcon" size="20" />
              </div>
              <div class="metrica-content">
                <h4 class="mb-0">{{ formatarMoeda(metricasConversao.ticket_medio) }}</h4>
                <small class="text-muted">Ticket Médio</small>
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Botão Ver Pedidos -->
        <div v-if="metricasConversao && metricasConversao.total_conversoes > 0" class="text-center mt-2">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="abrirModalPedidos"
          >
            <feather-icon icon="ListIcon" size="14" class="mr-50" />
            Ver Pedidos ({{ metricasConversao.total_conversoes }})
          </b-button>
        </div>
      </b-card>

      <!-- Aniversariantes de Hoje -->
      <b-card v-if="metricas && metricas.today_birthdays && metricas.today_birthdays.count > 0" class="aniversariantes-card shadow-sm">
        <h5 class="mb-3">
          <feather-icon icon="UsersIcon" size="20" class="mr-2" />
          Aniversariantes de Hoje ({{ metricas.today_birthdays.count }})
        </h5>
        <div class="aniversariantes-list">
          <div
            v-for="(aniversariante, index) in metricas.today_birthdays.sample"
            :key="index"
            class="aniversariante-item"
          >
            <feather-icon icon="UserIcon" size="16" class="text-muted mr-2" />
            <div class="flex-grow-1">
              <strong>{{ aniversariante.name }}</strong>
              <small class="text-muted d-block">{{ aniversariante.phone }}</small>
            </div>
            <b-badge variant="light-primary">
              {{ aniversariante.birthday_day }}
            </b-badge>
          </div>
          <div v-if="metricas.today_birthdays.has_more" class="text-center mt-2">
            <small class="text-muted">
              + {{ metricas.today_birthdays.count - metricas.today_birthdays.sample.length }} aniversariantes
            </small>
          </div>
        </div>
      </b-card>
    </template>

    <!-- Modal de Pedidos/Conversões -->
    <b-modal
      v-model="modalPedidosVisivel"
      title="Pedidos da Campanha de Aniversário"
      size="xl"
      centered
      scrollable
      @hidden="fecharModalPedidos"
    >
      <template v-if="carregandoPedidos">
        <div class="text-center py-5">
          <b-spinner variant="primary" />
          <p class="text-muted mt-3">Carregando pedidos...</p>
        </div>
      </template>

      <template v-else-if="pedidosConversao && pedidosConversao.length">
        <b-alert variant="info" show class="mb-3">
          <div class="d-flex align-items-center">
            <feather-icon icon="InfoIcon" size="16" class="mr-2" />
            <div>
              <strong>{{ pedidosConversao.length }}</strong> pedido(s) encontrado(s) na janela de {{ janelaAtribuicao }}h
            </div>
          </div>
        </b-alert>

        <div class="pedidos-container">
          <b-table
            :items="pedidosConversao"
            :fields="camposPedidos"
            :per-page="perPage"
            :current-page="currentPage"
            striped
            hover
            responsive
            show-empty
            empty-text="Nenhum pedido encontrado"
            class="pedidos-table"
          >
            <template #cell(created_at)="data">
              <small>{{ formatarDataCompleta(data.item.created_at) }}</small>
            </template>

            <template #cell(customer_name)="data">
              <div>
                <strong>{{ data.item.customer_name || 'N/A' }}</strong>
                <br>
                <small class="text-muted">{{ data.item.customer_phone || 'N/A' }}</small>
              </div>
            </template>

            <template #cell(order_value)="data">
              <strong class="text-success">{{ formatarMoeda(data.item.order_value) }}</strong>
            </template>

            <template #cell(time_to_conversion)="data">
              <small class="text-muted">
                {{ formatarTempoConversao(data.item.time_to_conversion) }}
              </small>
            </template>
          </b-table>
        </div>

        <div v-if="pedidosConversao.length > perPage" class="mt-3 d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a {{ Math.min(currentPage * perPage, pedidosConversao.length) }} de {{ pedidosConversao.length }} pedidos
          </small>
          <b-pagination
            v-model="currentPage"
            :total-rows="pedidosConversao.length"
            :per-page="perPage"
            size="sm"
            class="mb-0"
          />
        </div>
      </template>

      <template v-else>
        <div class="text-center py-5">
          <feather-icon icon="PackageIcon" size="48" class="text-muted mb-3" />
          <p class="text-muted">Nenhum pedido encontrado para esta campanha.</p>
        </div>
      </template>

      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <b-button
            variant="success"
            :disabled="!pedidosConversao || pedidosConversao.length === 0"
            @click="baixarCSVPedidos"
          >
            <feather-icon icon="DownloadIcon" size="14" class="mr-1" />
            Baixar CSV
          </b-button>
          <b-button variant="secondary" @click="fecharModalPedidos">
            Fechar
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Modal de Mensagens Enviadas -->
    <b-modal
      v-model="modalEnviosVisivel"
      title="Mensagens Enviadas"
      size="xl"
      centered
      scrollable
      @hidden="fecharModalEnvios"
    >
      <template v-if="carregandoEnvios">
        <div class="text-center py-5">
          <b-spinner variant="primary" />
          <p class="text-muted mt-3">Carregando mensagens enviadas...</p>
        </div>
      </template>

      <template v-else-if="mensagensEnviadas && mensagensEnviadas.length">
        <b-alert variant="info" show class="mb-3">
          <div class="d-flex align-items-center">
            <feather-icon icon="SendIcon" size="16" class="mr-2" />
            <div>
              <strong>{{ mensagensEnviadas.length }}</strong> mensagem(ns) enviada(s) nesta campanha
            </div>
          </div>
        </b-alert>

        <!-- Filtros -->
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <b-form-input
            v-model="filtroEnvios"
            placeholder="Buscar por nome ou telefone..."
            size="sm"
            style="max-width: 300px;"
          />
          <b-form-select
            v-model="filtroStatusEnvio"
            :options="opcoesStatusEnvio"
            size="sm"
            style="width: 150px;"
          />
        </div>

        <div class="envios-container">
          <b-table
            :items="mensagensEnviadasFiltradas"
            :fields="camposEnvios"
            :per-page="perPageEnvios"
            :current-page="currentPageEnvios"
            striped
            hover
            responsive
            show-empty
            empty-text="Nenhuma mensagem encontrada"
            class="envios-table"
          >
            <template #cell(sent_at)="data">
              <small>{{ formatarDataCompleta(data.item.sent_at) }}</small>
            </template>

            <template #cell(customer_name)="data">
              <div>
                <strong>{{ data.item.customer_name || 'N/A' }}</strong>
                <br>
                <small class="text-muted">{{ data.item.customer_phone || 'N/A' }}</small>
              </div>
            </template>

            <template #cell(template_type)="data">
              <b-badge :variant="data.item.template_type === 'recent' ? 'success' : 'info'">
                {{ data.item.template_type === 'recent' ? 'Recente' : 'Inativo' }}
              </b-badge>
            </template>

            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.item.status)">
                {{ getStatusLabel(data.item.status) }}
              </b-badge>
            </template>

            <template #cell(acoes)="data">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="verInformacoesCliente(data.item)"
              >
                <feather-icon icon="EyeIcon" size="14" class="mr-50" />
                Ver Informações
              </b-button>
            </template>
          </b-table>
        </div>

        <div v-if="mensagensEnviadasFiltradas.length > perPageEnvios" class="mt-3 d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Mostrando {{ (currentPageEnvios - 1) * perPageEnvios + 1 }} a {{ Math.min(currentPageEnvios * perPageEnvios, mensagensEnviadasFiltradas.length) }} de {{ mensagensEnviadasFiltradas.length }} mensagens
          </small>
          <b-pagination
            v-model="currentPageEnvios"
            :total-rows="mensagensEnviadasFiltradas.length"
            :per-page="perPageEnvios"
            size="sm"
            class="mb-0"
          />
        </div>
      </template>

      <template v-else>
        <div class="text-center py-5">
          <feather-icon icon="SendIcon" size="48" class="text-muted mb-3" />
          <p class="text-muted">Nenhuma mensagem enviada nesta campanha.</p>
        </div>
      </template>

      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <b-button
            variant="success"
            :disabled="!mensagensEnviadas || mensagensEnviadas.length === 0"
            @click="baixarCSVEnvios"
          >
            <feather-icon icon="DownloadIcon" size="14" class="mr-1" />
            Baixar CSV
          </b-button>
          <b-button variant="secondary" @click="fecharModalEnvios">
            Fechar
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Modal de Informações do Cliente -->
    <b-modal
      v-model="modalClienteVisivel"
      title="Informações do Cliente"
      size="lg"
      centered
      scrollable
      @hidden="fecharModalCliente"
    >
      <template v-if="carregandoCliente">
        <div class="text-center py-5">
          <b-spinner variant="primary" />
          <p class="text-muted mt-3">Carregando informações do cliente...</p>
        </div>
      </template>

      <template v-else-if="clienteSelecionado">
        <!-- Informações Básicas -->
        <div class="cliente-header mb-4">
          <div class="d-flex align-items-center">
            <div class="cliente-avatar mr-3">
              <feather-icon icon="UserIcon" size="32" />
            </div>
            <div>
              <h5 class="mb-1">{{ clienteSelecionado.customer_name || 'Nome não informado' }}</h5>
              <p class="text-muted mb-0">
                <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
                {{ clienteSelecionado.customer_phone || 'Telefone não informado' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Dados do Envio -->
        <b-card class="mb-3">
          <h6 class="mb-3">
            <feather-icon icon="SendIcon" size="16" class="mr-2" />
            Dados do Envio
          </h6>
          <b-row>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Data do Envio</small>
              <strong>{{ formatarDataCompleta(clienteSelecionado.sent_at) }}</strong>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Status</small>
              <b-badge :variant="getStatusVariant(clienteSelecionado.status)">
                {{ getStatusLabel(clienteSelecionado.status) }}
              </b-badge>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Template Enviado</small>
              <b-badge :variant="clienteSelecionado.template_type === 'recent' ? 'success' : 'info'">
                {{ clienteSelecionado.template_type === 'recent' ? 'Compradores Recentes' : 'Compradores Inativos' }}
              </b-badge>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Nome do Template</small>
              <strong>{{ clienteSelecionado.template_name || 'N/A' }}</strong>
            </b-col>
          </b-row>
        </b-card>

        <!-- Dados do Cliente -->
        <b-card class="mb-3">
          <h6 class="mb-3">
            <feather-icon icon="UserIcon" size="16" class="mr-2" />
            Dados do Cliente
          </h6>
          <b-row>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Data de Aniversário</small>
              <strong>{{ formatarDataAniversario(clienteSelecionado.birthday) }}</strong>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Email</small>
              <strong>{{ clienteSelecionado.customer_email || 'Não informado' }}</strong>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Última Compra</small>
              <strong>{{ clienteSelecionado.last_purchase_date ? formatarDataCompleta(clienteSelecionado.last_purchase_date) : 'Sem compras registradas' }}</strong>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Total de Compras</small>
              <strong>{{ clienteSelecionado.total_purchases || 0 }} compra(s)</strong>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Valor Total Gasto</small>
              <strong class="text-success">{{ formatarMoeda(clienteSelecionado.total_spent) }}</strong>
            </b-col>
            <b-col md="6" class="mb-2">
              <small class="text-muted d-block">Ticket Médio</small>
              <strong>{{ formatarMoeda(clienteSelecionado.average_ticket) }}</strong>
            </b-col>
          </b-row>
        </b-card>

        <!-- Histórico de Compras Recente -->
        <b-card v-if="clienteSelecionado.recent_orders && clienteSelecionado.recent_orders.length > 0">
          <h6 class="mb-3">
            <feather-icon icon="ShoppingBagIcon" size="16" class="mr-2" />
            Últimas Compras
          </h6>
          <div class="compras-list">
            <div
              v-for="(compra, index) in clienteSelecionado.recent_orders"
              :key="index"
              class="compra-item d-flex justify-content-between align-items-center py-2"
              :class="{ 'border-bottom': index < clienteSelecionado.recent_orders.length - 1 }"
            >
              <div>
                <strong>Pedido #{{ compra.order_id }}</strong>
                <small class="text-muted d-block">{{ formatarDataCompleta(compra.created_at) }}</small>
              </div>
              <strong class="text-success">{{ formatarMoeda(compra.order_value) }}</strong>
            </div>
          </div>
        </b-card>
      </template>

      <template v-else>
        <div class="text-center py-5">
          <feather-icon icon="AlertCircleIcon" size="48" class="text-muted mb-3" />
          <p class="text-muted">Não foi possível carregar as informações do cliente.</p>
        </div>
      </template>

      <template #modal-footer>
        <b-button variant="secondary" @click="fecharModalCliente">
          Fechar
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BCard,
  BCol,
  BFormInput,
  BFormSelect,
  BModal,
  BPagination,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import {
  buscarStatusCampanhaAniversario,
  buscarCampanhaAniversarioAtiva,
  buscarMetricasConversaoAniversario,
  buscarPedidosAniversario,
  buscarMensagensEnviadasAniversario,
  buscarInfoClienteAniversario,
} from '@/functions/basics/birthday-campaigns'

export default {
  name: 'MetricasAniversario',
  components: {
    BAlert,
    BBadge,
    BBreadcrumb,
    BBreadcrumbItem,
    BButton,
    BCard,
    BCol,
    BFormInput,
    BFormSelect,
    BModal,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    FeatherIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      lojaSelecionada: null,
      nomeLojaSelecionada: '',
      campanhaAtiva: null,
      metricas: null,
      carregando: false,
      // Conversões
      janelaAtribuicao: 168,
      opcoesJanelaAtribuicao: [
        { value: 24, text: '24h (1 dia)' },
        { value: 48, text: '48h (2 dias)' },
        { value: 72, text: '72h (3 dias)' },
        { value: 120, text: '120h (5 dias)' },
        { value: 168, text: '168h (7 dias)' },
      ],
      metricasConversao: null,
      carregandoConversoes: false,
      // Modal de Pedidos
      modalPedidosVisivel: false,
      pedidosConversao: [],
      carregandoPedidos: false,
      currentPage: 1,
      perPage: 10,
      camposPedidos: [
        { key: 'created_at', label: 'Data', sortable: true },
        { key: 'order_id', label: 'Pedido', sortable: true },
        { key: 'customer_name', label: 'Cliente', sortable: true },
        { key: 'order_value', label: 'Valor', sortable: true },
        { key: 'time_to_conversion', label: 'Tempo até Conversão', sortable: true },
      ],
      // Modal de Envios
      modalEnviosVisivel: false,
      mensagensEnviadas: [],
      carregandoEnvios: false,
      currentPageEnvios: 1,
      perPageEnvios: 10,
      filtroEnvios: '',
      filtroStatusEnvio: 'all',
      opcoesStatusEnvio: [
        { value: 'all', text: 'Todos' },
        { value: 'sent', text: 'Enviados' },
        { value: 'scheduled', text: 'Agendados' },
        { value: 'errors', text: 'Erros (Bloqueados, Falhas, Cancelados)' },
        { value: 'blocked', text: '→ Bloqueados' },
        { value: 'failed', text: '→ Falhas' },
        { value: 'cancelled', text: '→ Cancelados' },
      ],
      camposEnvios: [
        { key: 'sent_at', label: 'Data Envio', sortable: true },
        { key: 'customer_name', label: 'Cliente', sortable: true },
        { key: 'template_type', label: 'Tipo', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'acoes', label: 'Ações', sortable: false },
      ],
      // Modal de Informações do Cliente
      modalClienteVisivel: false,
      clienteSelecionado: null,
      carregandoCliente: false,
    }
  },
  computed: {
    mensagensEnviadasFiltradas() {
      if (!this.mensagensEnviadas) return []
      
      let filtradas = [...this.mensagensEnviadas]
      
      // Filtro por status
      if (this.filtroStatusEnvio !== 'all') {
        if (this.filtroStatusEnvio === 'errors') {
          filtradas = filtradas.filter(m => ['blocked', 'failed', 'cancelled'].includes(m.status))
        } else {
          filtradas = filtradas.filter(m => m.status === this.filtroStatusEnvio)
        }
      }
      
      // Filtro por texto (nome ou telefone)
      if (this.filtroEnvios) {
        const termo = this.filtroEnvios.toLowerCase()
        filtradas = filtradas.filter(m => 
          (m.customer_name && m.customer_name.toLowerCase().includes(termo)) ||
          (m.customer_phone && m.customer_phone.toLowerCase().includes(termo))
        )
      }
      
      return filtradas
    },
    totalErros() {
      if (!this.metricas) return 0
      const blocked = this.metricas.total_blocked || 0
      const failed = this.metricas.total_failed || 0
      const cancelled = this.metricas.total_cancelled || 0
      return blocked + failed + cancelled
    },
  },
  async created() {
    this.lojaSelecionada = this.$route.query.loja || localStorage.getItem('lojaSelecionada')
    this.nomeLojaSelecionada = this.$route.query.nomeLoja || localStorage.getItem('nomeLojaSelecionada') || 'Loja'

    if (!this.lojaSelecionada) {
      this.$bvToast.toast('Selecione uma loja primeiro', {
        title: 'Loja Obrigatória',
        variant: 'warning',
        solid: true,
      })
      this.$router.push({ name: 'campaign-ia' })
      return
    }

    await this.verificarCampanhaAtiva()
  },
  methods: {
    async verificarCampanhaAtiva() {
      this.carregando = true
      try {
        const campanha = await buscarCampanhaAniversarioAtiva(this.lojaSelecionada)
        
        if (campanha) {
          this.campanhaAtiva = campanha
          await this.carregarMetricas(campanha.id)
        }
      } catch (error) {
        console.error('Erro ao verificar campanha ativa:', error)
      } finally {
        this.carregando = false
      }
    },

    async carregarMetricas(campaignId) {
      try {
        const status = await buscarStatusCampanhaAniversario(campaignId, this.lojaSelecionada)
        this.metricas = status.metrics || null
        
        await this.carregarMetricasConversao()
      } catch (error) {
        console.error('Erro ao carregar métricas:', error)
      }
    },

    async carregarMetricasConversao() {
      if (!this.campanhaAtiva) return
      
      this.carregandoConversoes = true
      try {
        const conversoes = await buscarMetricasConversaoAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
          this.janelaAtribuicao
        )
        this.metricasConversao = conversoes
      } catch (error) {
        console.error('Erro ao carregar métricas de conversão:', error)
        this.metricasConversao = {
          total_conversoes: 0,
          taxa_conversao: 0,
          valor_total: 0,
          ticket_medio: 0,
        }
      } finally {
        this.carregandoConversoes = false
      }
    },

    async abrirModalPedidos() {
      this.modalPedidosVisivel = true
      this.carregandoPedidos = true
      this.currentPage = 1
      
      try {
        const pedidos = await buscarPedidosAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
          this.janelaAtribuicao
        )
        this.pedidosConversao = pedidos || []
      } catch (error) {
        console.error('Erro ao carregar pedidos:', error)
        this.$bvToast.toast('Erro ao carregar pedidos da campanha', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.pedidosConversao = []
      } finally {
        this.carregandoPedidos = false
      }
    },

    fecharModalPedidos() {
      this.modalPedidosVisivel = false
      this.pedidosConversao = []
      this.currentPage = 1
    },

    baixarCSVPedidos() {
      if (!this.pedidosConversao || this.pedidosConversao.length === 0) {
        this.$bvToast.toast('Nenhum pedido para exportar', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      try {
        const headers = [
          'Data',
          'ID do Pedido',
          'Cliente',
          'Telefone',
          'Valor',
          'Tempo até Conversão (min)',
        ]

        const rows = this.pedidosConversao.map(pedido => [
          this.formatarDataCompleta(pedido.created_at),
          pedido.order_id || 'N/A',
          pedido.customer_name || 'N/A',
          pedido.customer_phone || 'N/A',
          pedido.order_value || '0',
          pedido.time_to_conversion || '0',
        ])

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        const nomeCampanha = this.campanhaAtiva?.name || 'campanha-aniversario'
        const timestamp = new Date().toISOString().split('T')[0]
        const nomeArquivo = `pedidos_${nomeCampanha}_${timestamp}.csv`
        
        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)

        this.$bvToast.toast('CSV baixado com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Erro ao gerar CSV:', error)
        this.$bvToast.toast('Erro ao gerar arquivo CSV', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    formatarMoeda(valor) {
      if (valor === null || valor === undefined) return 'R$ 0,00'
      const numero = typeof valor === 'string' ? parseFloat(valor) : valor
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(numero)
    },

    formatarDataCompleta(data) {
      if (!data) return 'N/A'
      return new Date(data).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    formatarTempoConversao(minutos) {
      if (!minutos || minutos === 0) return 'Imediato'
      
      const horas = Math.floor(minutos / 60)
      const mins = Math.floor(minutos % 60)
      
      if (horas > 0) {
        return `${horas}h ${mins}min`
      }
      
      return `${mins}min`
    },

    formatarDataAniversario(data) {
      if (!data) return 'Não informado'
      const date = new Date(data)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
      })
    },

    formatarHorario(time) {
      if (!time) return '--:--'
      return time.substring(0, 5)
    },

    getStatusVariant(status) {
      const variants = {
        scheduled: 'info',
        sent: 'success',
        failed: 'danger',
        blocked: 'warning',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusLabel(status) {
      const labels = {
        scheduled: 'Agendado',
        sent: 'Enviado',
        failed: 'Falha',
        blocked: 'Bloqueado',
        cancelled: 'Cancelado',
      }
      return labels[status] || status
    },

    async abrirModalEnviosComFiltro(status = 'all') {
      this.modalEnviosVisivel = true
      this.carregandoEnvios = true
      this.currentPageEnvios = 1
      this.filtroEnvios = ''
      this.filtroStatusEnvio = status
      
      try {
        const mensagens = await buscarMensagensEnviadasAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada
        )
        this.mensagensEnviadas = mensagens || []
      } catch (error) {
        console.error('Erro ao carregar mensagens enviadas:', error)
        this.$bvToast.toast('Erro ao carregar mensagens enviadas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
        this.mensagensEnviadas = []
      } finally {
        this.carregandoEnvios = false
      }
    },

    fecharModalEnvios() {
      this.modalEnviosVisivel = false
      this.mensagensEnviadas = []
      this.currentPageEnvios = 1
      this.filtroEnvios = ''
      this.filtroStatusEnvio = 'all'
    },

    async verInformacoesCliente(envio) {
      this.modalClienteVisivel = true
      this.carregandoCliente = true
      this.clienteSelecionado = null
      
      try {
        const infoCliente = await buscarInfoClienteAniversario(
          this.campanhaAtiva.id,
          this.lojaSelecionada,
          envio.lead_id
        )
        this.clienteSelecionado = {
          ...envio,
          ...infoCliente,
        }
      } catch (error) {
        console.error('Erro ao carregar informações do cliente:', error)
        this.clienteSelecionado = envio
      } finally {
        this.carregandoCliente = false
      }
    },

    fecharModalCliente() {
      this.modalClienteVisivel = false
      this.clienteSelecionado = null
    },

    baixarCSVEnvios() {
      if (!this.mensagensEnviadas || this.mensagensEnviadas.length === 0) {
        this.$bvToast.toast('Nenhuma mensagem para exportar', {
          title: 'Aviso',
          variant: 'warning',
          solid: true,
        })
        return
      }

      try {
        const headers = [
          'Data Envio',
          'Nome do Cliente',
          'Telefone',
          'Tipo Template',
          'Status',
          'Data Aniversário',
        ]

        const rows = this.mensagensEnviadas.map(msg => [
          this.formatarDataCompleta(msg.sent_at),
          msg.customer_name || 'N/A',
          msg.customer_phone || 'N/A',
          msg.template_type === 'recent' ? 'Recente' : 'Inativo',
          this.getStatusLabel(msg.status),
          this.formatarDataAniversario(msg.birthday),
        ])

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        const BOM = '\uFEFF'
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })

        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        const nomeCampanha = this.campanhaAtiva?.name || 'campanha-aniversario'
        const timestamp = new Date().toISOString().split('T')[0]
        const nomeArquivo = `envios_${nomeCampanha}_${timestamp}.csv`
        
        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)

        this.$bvToast.toast('CSV baixado com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Erro ao gerar CSV:', error)
        this.$bvToast.toast('Erro ao gerar arquivo CSV', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style scoped>
.metricas-aniversario-container {
  padding: 2rem;
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #5e5873;
  display: flex;
  align-items: center;
}

.breadcrumb-custom {
  background: transparent;
  padding: 0;
  margin: 0;
}

.loja-info {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.status-badge-active {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28c76f 0%, #1f9d57 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.config-info {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.info-item {
  display: flex;
  align-items: center;
}

.metrica-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.metrica-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.metrica-card-clickable {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.metrica-card-clickable:hover {
  border-color: #9A3092;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(154, 48, 146, 0.15);
}

.metrica-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
}

.metrica-content h4 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5e5873;
}

.conversao-card {
  border-left: 3px solid #9A3092;
}

.aniversariantes-list {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.aniversariante-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.aniversariante-item:last-child {
  margin-bottom: 0;
}

.empty-icon {
  width: 120px;
  height: 120px;
  margin: 0 auto;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cursor-pointer {
  cursor: pointer;
}

.pedidos-container,
.envios-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.pedidos-table,
.envios-table {
  font-size: 0.9rem;
  margin-bottom: 0;
}

.pedidos-table ::v-deep th,
.envios-table ::v-deep th {
  background-color: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  color: #6e6b7b;
  position: sticky;
  top: 0;
  z-index: 10;
}

.pedidos-table ::v-deep td,
.envios-table ::v-deep td {
  vertical-align: middle;
}

.pedidos-table ::v-deep tr:hover,
.envios-table ::v-deep tr:hover {
  background-color: #f8f9fa;
}

.cliente-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.cliente-avatar {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #9A3092 0%, #61185b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.compras-list .compra-item {
  transition: background 0.2s ease;
}

.compras-list .compra-item:hover {
  background: #f8f9fa;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .metricas-aniversario-container {
    padding: 1rem;
  }

  .page-header {
    padding: 1.5rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .metrica-card {
    margin-bottom: 1rem;
  }

  .cliente-header {
    padding: 1rem;
  }

  .cliente-avatar {
    width: 48px;
    height: 48px;
  }
}
</style>
